// Pantalla del kine: prescribir nueva rutina (= crear plan + plan_ejercicios)
// para un paciente. Camino C — sin templates intermedios. Ver
// docs/decisions/rutinas-prescription-flow.md.
//
// Layout 3-cols (heredado del mock visual, ahora con data real):
//   - Izq: identidad del paciente + notas del plan.
//   - Mid: catálogo de ejercicios prescriptibles (filtrado por
//          schema_path IS NOT NULL para Sprint 3 cuando aparezcan
//          customs sin calibrar).
//   - Der: plan en construcción con state local + selector L-M-M-J-V-S-D
//          de frecuencia (solo cuenta días, qué días específicos no se
//          persiste — Sprint 4+ si aparece caso de recordatorios).
//
// Sprint 3 Approach A: el kine llama la RPC `prescribir_rutina_a_paciente`
// (0017) que hace find-or-create del contenedor invisible + delega a
// crear_plan_con_ejercicios. La UI no expone la entidad tratamiento.

import { useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router";
import { ChevronRight, Plus, X } from "lucide-react";

import { Avatar } from "../components/Avatar";
import { KineHeader } from "../components/kine/KineHeader";
import { KineSidebar } from "../components/kine/KineSidebar";
import { useDetallePaciente } from "@/lib/supabase/queries/useDetallePaciente";
import { useEjercicios, type Ejercicio } from "@/lib/supabase/queries/useEjercicios";
import {
  getExerciseSchema,
  isVideoOnlyExercise,
} from "@/lib/pose/exerciseSchemas";
import { PlayableThumbnail } from "../components/patients/PlayableThumbnail";
import { ExerciseStatusBadges } from "../components/patients/ExerciseStatusBadges";
import { VideoTutorialModal } from "../components/sessions/VideoTutorialModal";
import {
  useCrearPlan,
  type CrearPlanEjercicioInput,
} from "@/lib/supabase/queries/useCrearPlan";

const DIAS_SEMANA: Array<{ key: number; label: string }> = [
  { key: 1, label: "L" },
  { key: 2, label: "M" },
  { key: 3, label: "M" },
  { key: 4, label: "J" },
  { key: 5, label: "V" },
  { key: 6, label: "S" },
  { key: 0, label: "D" },
];

interface EjercicioDraft {
  ejercicio_id: string;
  ejercicio_nombre: string;
  orden: number;
  reps_objetivo: number;
  series_objetivo: number;
  descanso_seg: number | null;
}

const DEFAULT_REPS = 12;
const DEFAULT_SERIES = 3;
const DEFAULT_DESCANSO = 60;

export function PrescribirRutina() {
  const navigate = useNavigate();
  const { patientId } = useParams<{ patientId: string }>();
  const [drawerOpen, setDrawerOpen] = useState(false);

  const { data: paciente, errorKind: pacienteError } =
    useDetallePaciente(patientId);
  const { data: ejercicios } = useEjercicios();
  const { crear, loading: submitting, error: submitError } = useCrearPlan();

  const [nombrePlan, setNombrePlan] = useState("");
  const [diasSeleccionados, setDiasSeleccionados] = useState<Set<number>>(
    new Set(),
  );
  const [draftEjercicios, setDraftEjercicios] = useState<EjercicioDraft[]>([]);
  const [notasKine, setNotasKine] = useState("");

  // Filtro defensivo Sprint 3 — hoy todos los ejercicios tienen schema_path
  // (NOT NULL en schema actual). Cuando se agregue catálogo custom sin
  // calibrar, este filtro los oculta del selector.
  const ejerciciosPrescriptibles = useMemo(
    () => (ejercicios ?? []).filter((e) => e.schema_path !== null),
    [ejercicios],
  );

  // Map ejercicio_id → count de instancias en el draft. Usado por el
  // catálogo para mostrar badge "En plan ×N" — feedback visual contra
  // doble-click accidental que agrega múltiples instancias del mismo
  // ejercicio. NO bloquea el botón "Añadir" (caso clínico real:
  // calentamiento + pico de fuerza con el mismo ejercicio).
  const countsByEjercicioId = useMemo(() => {
    const m = new Map<string, number>();
    for (const e of draftEjercicios) {
      m.set(e.ejercicio_id, (m.get(e.ejercicio_id) ?? 0) + 1);
    }
    return m;
  }, [draftEjercicios]);

  // Edge: paciente no encontrado / id inválido.
  if (pacienteError === "not-found") {
    return (
      <FullPageMessage
        titulo="Paciente no encontrado"
        body="El paciente con ese id no existe o no es accesible."
        onVolver={() => navigate("/")}
      />
    );
  }

  function agregarEjercicio(ej: Ejercicio) {
    setDraftEjercicios((prev) => {
      const orden = prev.length + 1;
      // Video-only: defaults 1/1/0 (cumplen NOT NULL del schema DB pero
      // el paciente nunca los ve — sigue el video tutorial). El kine
      // tampoco edita estos campos en el form simplificado.
      const isVideoOnly = isVideoOnlyExercise(ej.id);
      return [
        ...prev,
        {
          ejercicio_id: ej.id,
          ejercicio_nombre: ej.nombre,
          orden,
          reps_objetivo: isVideoOnly ? 1 : DEFAULT_REPS,
          series_objetivo: isVideoOnly ? 1 : DEFAULT_SERIES,
          descanso_seg: isVideoOnly ? 0 : DEFAULT_DESCANSO,
        },
      ];
    });
  }

  function quitarEjercicio(orden: number) {
    setDraftEjercicios((prev) =>
      prev
        .filter((e) => e.orden !== orden)
        .map((e, i) => ({ ...e, orden: i + 1 })),
    );
  }

  function actualizarEjercicio(
    orden: number,
    patch: Partial<EjercicioDraft>,
  ) {
    setDraftEjercicios((prev) =>
      prev.map((e) => (e.orden === orden ? { ...e, ...patch } : e)),
    );
  }

  function toggleDia(diaKey: number) {
    setDiasSeleccionados((prev) => {
      const next = new Set(prev);
      if (next.has(diaKey)) next.delete(diaKey);
      else next.add(diaKey);
      return next;
    });
  }

  const frecuenciaSemanal = diasSeleccionados.size;
  const formValid =
    nombrePlan.trim().length > 0 &&
    draftEjercicios.length > 0 &&
    frecuenciaSemanal >= 1 &&
    !!patientId;

  async function handleSubmit() {
    if (!formValid || !patientId) return;
    const ejerciciosInput: CrearPlanEjercicioInput[] = draftEjercicios.map(
      (e) => ({
        ejercicio_id: e.ejercicio_id,
        orden: e.orden,
        reps_objetivo: e.reps_objetivo,
        series_objetivo: e.series_objetivo,
        descanso_seg: e.descanso_seg,
        frecuencia_semanal: frecuenciaSemanal,
        notas_kine: null,
      }),
    );
    const planId = await crear({
      pacienteId: patientId,
      rutinaOrigenId: null,
      nombre: nombrePlan.trim(),
      fechaInicio: new Date().toISOString().slice(0, 10),
      notasKine: notasKine.trim() || null,
      ejercicios: ejerciciosInput,
    });
    if (planId) {
      navigate(`/paciente/${patientId}`);
    }
  }

  return (
    <div className="min-h-screen bg-background transition-colors duration-200">
      <KineHeader
        kineNombre={paciente?.kineNombre}
        onMenuClick={() => setDrawerOpen(true)}
      />

      <div className="flex">
        <KineSidebar
          activeKey="pacientes"
          mobileOpen={drawerOpen}
          onMobileClose={() => setDrawerOpen(false)}
        />

        <div className="flex-1 p-4 md:p-8 min-w-0">
          <div className="max-w-[1400px]">
            {/* Breadcrumb */}
            <div className="flex items-center gap-2 text-sm text-muted-foreground mb-6">
              <Link
                to="/"
                className="hover:text-primary transition-colors duration-200"
              >
                Pacientes
              </Link>
              <ChevronRight className="w-4 h-4" strokeWidth={1.5} />
              <Link
                to={`/paciente/${patientId}`}
                className="hover:text-primary transition-colors duration-200"
              >
                {paciente
                  ? `${paciente.paciente.nombre} ${paciente.paciente.apellido}`
                  : "Paciente"}
              </Link>
              <ChevronRight className="w-4 h-4" strokeWidth={1.5} />
              <span>Nueva rutina</span>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-[280px_1fr_360px] gap-6">
              {/* Panel izq: paciente */}
              <PacienteCard
                paciente={paciente}
                notasKine={notasKine}
                onNotasChange={setNotasKine}
              />

              {/* Panel mid: catálogo */}
              <CatalogoEjercicios
                ejercicios={ejerciciosPrescriptibles}
                onAgregar={agregarEjercicio}
                countsByEjercicioId={countsByEjercicioId}
              />

              {/* Panel der: rutina en construcción */}
              <RutinaEnConstruccion
                nombre={nombrePlan}
                onNombreChange={setNombrePlan}
                ejercicios={draftEjercicios}
                onActualizarEjercicio={actualizarEjercicio}
                onQuitarEjercicio={quitarEjercicio}
                diasSeleccionados={diasSeleccionados}
                onToggleDia={toggleDia}
                onSubmit={handleSubmit}
                submitting={submitting}
                submitError={submitError}
                formValid={formValid}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Subcomponents ────────────────────────────────────────────────────────

function FullPageMessage({
  titulo,
  body,
  onVolver,
}: {
  titulo: string;
  body: string;
  onVolver: () => void;
}) {
  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-6">
      <div className="max-w-md text-center bg-card border border-border rounded-2xl p-8">
        <h1 className="text-2xl font-semibold mb-2">{titulo}</h1>
        <p className="text-sm text-muted-foreground mb-6">{body}</p>
        <button
          onClick={onVolver}
          className="bg-primary text-primary-foreground px-5 py-2 rounded-full text-sm font-semibold"
        >
          Volver
        </button>
      </div>
    </div>
  );
}

function PacienteCard({
  paciente,
  notasKine,
  onNotasChange,
}: {
  paciente: ReturnType<typeof useDetallePaciente>["data"];
  notasKine: string;
  onNotasChange: (v: string) => void;
}) {
  const fullName = paciente
    ? `${paciente.paciente.nombre} ${paciente.paciente.apellido}`
    : "Cargando…";

  return (
    <div className="bg-card rounded-2xl p-6 border border-border shadow-[0_1px_2px_rgba(15,23,32,0.04),0_1px_3px_rgba(15,23,32,0.06)] h-fit">
      <div className="flex items-center gap-4 pb-4 border-b border-border">
        <Avatar name={fullName} size="lg" colorIndex={0} />
        <div className="min-w-0">
          <h3 className="font-semibold truncate">{fullName}</h3>
        </div>
      </div>

      <div className="pt-4 space-y-4">
        <div>
          <div className="text-xs text-muted-foreground tracking-[0.08em] uppercase mb-2">
            Notas del plan (opcional)
          </div>
          <textarea
            value={notasKine}
            onChange={(e) => onNotasChange(e.target.value)}
            className="w-full h-24 p-3 bg-input-background border border-border rounded-lg text-sm resize-none transition-all duration-200 ease-out focus:ring-2 focus:ring-ring focus:border-transparent"
            placeholder="Observaciones..."
          />
        </div>
      </div>
    </div>
  );
}

function CatalogoEjercicios({
  ejercicios,
  onAgregar,
  countsByEjercicioId,
}: {
  ejercicios: Ejercicio[];
  onAgregar: (ej: Ejercicio) => void;
  countsByEjercicioId: Map<string, number>;
}) {
  // Video activo del modal tutorial. Reproducible desde el thumbnail
  // de cualquier card del catálogo. State local al CatalogoEjercicios
  // — el modal se renderea acá mismo (fuera del grid).
  const [activeVideo, setActiveVideo] = useState<{
    url: string;
    name: string;
  } | null>(null);

  return (
    <div className="space-y-4">
      <h1 className="text-2xl">Biblioteca de ejercicios</h1>

      {ejercicios.length === 0 ? (
        <p className="text-sm text-muted-foreground py-8 text-center">
          No hay ejercicios disponibles en el catálogo.
        </p>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {ejercicios.map((ej) => {
            const count = countsByEjercicioId.get(ej.id) ?? 0;
            const schema = getExerciseSchema(ej.id);
            const videoUrl = schema?.video_url;
            return (
              <div
                key={ej.id}
                className="bg-card rounded-2xl p-4 border border-border shadow-[0_1px_2px_rgba(15,23,32,0.04),0_1px_3px_rgba(15,23,32,0.06)] flex flex-col"
              >
                <div className="mb-3">
                  <PlayableThumbnail
                    videoUrl={videoUrl}
                    exerciseName={ej.nombre}
                    onClick={
                      videoUrl
                        ? () => setActiveVideo({ url: videoUrl, name: ej.nombre })
                        : undefined
                    }
                    className="aspect-square"
                  />
                </div>
                <div className="flex items-start gap-2 mb-1">
                  <h4 className="font-semibold flex-1 min-w-0">{ej.nombre}</h4>
                  {count >= 1 && (
                    <span
                      className="shrink-0 text-[10px] px-2 py-0.5 bg-secondary text-secondary-foreground rounded-full font-medium tabular-nums"
                      title={`Esta rutina ya incluye ${count} ${count === 1 ? "instancia" : "instancias"} de este ejercicio`}
                    >
                      En plan ×{count}
                    </span>
                  )}
                </div>
                {/* Badges de estado: principalmente "Sin detección de cámara"
                    es lo crítico al prescribir (el kine necesita saber si
                    el paciente va a tener feedback de pose o solo video).
                    "Calibración preliminar" también — discoverability honesta
                    sobre estado clínico del schema. */}
                <ExerciseStatusBadges schema={schema} />
                {ej.region_anatomica && (
                  <p className="text-sm text-primary mt-2 mb-2 font-medium capitalize">
                    {ej.region_anatomica}
                  </p>
                )}
                {ej.descripcion && (
                  <p className="text-xs text-muted-foreground mb-3 line-clamp-2">
                    {ej.descripcion}
                  </p>
                )}
                <button
                  onClick={() => onAgregar(ej)}
                  className="mt-auto w-full bg-accent text-accent-foreground py-2 rounded-lg text-sm font-semibold flex items-center justify-center gap-1 transition-all duration-200 ease-out hover:scale-[1.02]"
                >
                  <Plus className="w-4 h-4" strokeWidth={1.5} />
                  Añadir
                </button>
              </div>
            );
          })}
        </div>
      )}

      <VideoTutorialModal
        videoUrl={activeVideo?.url}
        exerciseName={activeVideo?.name ?? ""}
        open={activeVideo !== null}
        onClose={() => setActiveVideo(null)}
      />
    </div>
  );
}

function RutinaEnConstruccion({
  nombre,
  onNombreChange,
  ejercicios,
  onActualizarEjercicio,
  onQuitarEjercicio,
  diasSeleccionados,
  onToggleDia,
  onSubmit,
  submitting,
  submitError,
  formValid,
}: {
  nombre: string;
  onNombreChange: (v: string) => void;
  ejercicios: EjercicioDraft[];
  onActualizarEjercicio: (orden: number, patch: Partial<EjercicioDraft>) => void;
  onQuitarEjercicio: (orden: number) => void;
  diasSeleccionados: Set<number>;
  onToggleDia: (key: number) => void;
  onSubmit: () => void;
  submitting: boolean;
  submitError: Error | null;
  formValid: boolean;
}) {
  const frecuencia = diasSeleccionados.size;

  return (
    <div className="bg-card rounded-2xl p-6 border border-border shadow-[0_1px_2px_rgba(15,23,32,0.04),0_1px_3px_rgba(15,23,32,0.06)] lg:sticky lg:top-6 h-fit">
      <h3 className="mb-4 font-semibold">Rutina en construcción</h3>

      <div className="mb-4">
        <label className="text-xs text-muted-foreground tracking-[0.08em] uppercase mb-2 block">
          Nombre
        </label>
        <input
          type="text"
          value={nombre}
          onChange={(e) => onNombreChange(e.target.value)}
          placeholder="Ej. Rutina manguito fase 1"
          className="w-full px-3 py-2 bg-input-background border border-border rounded-lg text-sm focus:ring-2 focus:ring-ring focus:border-transparent"
        />
      </div>

      <div className="text-xs text-muted-foreground tracking-[0.08em] uppercase mb-2">
        Ejercicios ({ejercicios.length})
      </div>
      <div className="space-y-3 mb-6">
        {ejercicios.length === 0 ? (
          <p className="text-sm text-muted-foreground p-4 bg-muted/30 rounded-lg text-center">
            Agregá ejercicios desde la biblioteca.
          </p>
        ) : (
          ejercicios.map((ej) => (
            <EjercicioDraftRow
              key={ej.orden}
              ejercicio={ej}
              onUpdate={(patch) => onActualizarEjercicio(ej.orden, patch)}
              onQuitar={() => onQuitarEjercicio(ej.orden)}
            />
          ))
        )}
      </div>

      <div className="mb-6">
        <div className="text-xs text-muted-foreground tracking-[0.08em] uppercase mb-2">
          Frecuencia semanal ({frecuencia} {frecuencia === 1 ? "día" : "días"})
        </div>
        <div className="flex justify-between gap-1">
          {DIAS_SEMANA.map((d) => {
            const active = diasSeleccionados.has(d.key);
            return (
              <button
                key={`${d.key}-${d.label}`}
                onClick={() => onToggleDia(d.key)}
                className={`flex-1 h-10 rounded-full font-semibold text-sm transition-all duration-200 ease-out ${
                  active
                    ? "bg-primary text-primary-foreground"
                    : "bg-muted text-muted-foreground hover:bg-muted/80"
                }`}
              >
                {d.label}
              </button>
            );
          })}
        </div>
      </div>

      {submitError && (
        <p className="text-xs text-orange-600 mb-3">
          {submitError.message}
        </p>
      )}

      <button
        onClick={onSubmit}
        disabled={!formValid || submitting}
        className="w-full bg-accent text-accent-foreground py-3 rounded-[20px] font-semibold disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 ease-out hover:scale-[1.02]"
      >
        {submitting ? "Guardando…" : "Prescribir rutina"}
      </button>
    </div>
  );
}

function EjercicioDraftRow({
  ejercicio,
  onUpdate,
  onQuitar,
}: {
  ejercicio: EjercicioDraft;
  onUpdate: (patch: Partial<EjercicioDraft>) => void;
  onQuitar: () => void;
}) {
  const isVideoOnly = isVideoOnlyExercise(ejercicio.ejercicio_id);

  // Validación blanda: warning visual fuera de rangos clínicos comunes.
  // No aplica para video_only (no se muestran los inputs).
  const repsWarning =
    !isVideoOnly &&
    (ejercicio.reps_objetivo < 1 || ejercicio.reps_objetivo > 50);
  const seriesWarning =
    !isVideoOnly &&
    (ejercicio.series_objetivo < 1 || ejercicio.series_objetivo > 10);

  return (
    <div className="p-2.5 bg-muted/30 rounded-lg">
      <div className="flex items-center justify-between mb-1.5 gap-2">
        <div className="flex items-center gap-2 flex-1 min-w-0">
          <span className="text-muted-foreground font-medium tabular-nums text-sm">
            {ejercicio.orden}.
          </span>
          <p className="text-sm font-medium truncate">
            {ejercicio.ejercicio_nombre}
          </p>
        </div>
        <button
          onClick={onQuitar}
          aria-label="Quitar ejercicio"
          className="text-muted-foreground hover:text-destructive transition-colors"
        >
          <X className="w-4 h-4" strokeWidth={1.5} />
        </button>
      </div>

      {isVideoOnly ? (
        // Form simplificado para video_only: solo badge informativo.
        // reps/series/descanso se persisten como 1/1/0 (defaults seteados
        // en agregarEjercicio). El paciente sigue el video tutorial, no
        // tiene métricas de cámara — los inputs no aplican.
        <p className="text-[11px] text-muted-foreground italic">
          Video tutorial — sin métricas de cámara
        </p>
      ) : (
        <>
          <div className="grid grid-cols-3 gap-2">
            <NumericField
              label="Series"
              value={ejercicio.series_objetivo}
              onChange={(v) => onUpdate({ series_objetivo: v })}
              warning={seriesWarning}
            />
            <NumericField
              label="Reps"
              suffix="reps"
              value={ejercicio.reps_objetivo}
              onChange={(v) => onUpdate({ reps_objetivo: v })}
              warning={repsWarning}
            />
            <NumericField
              label="Descanso"
              suffix="seg"
              value={ejercicio.descanso_seg ?? 0}
              onChange={(v) => onUpdate({ descanso_seg: v })}
              warning={false}
            />
          </div>
          {(repsWarning || seriesWarning) && (
            <p className="text-[11px] text-amber-600 mt-1.5">
              Fuera del rango común (series 1-10, reps 1-50). Confirmá si es
              intencional.
            </p>
          )}
        </>
      )}
    </div>
  );
}

function NumericField({
  label,
  value,
  onChange,
  warning,
  suffix,
}: {
  label: string;
  value: number;
  onChange: (v: number) => void;
  warning: boolean;
  /** Unidad opcional pegada al input (ej. "reps", "seg"). Sin sufijo
   *  si la unidad ya está implícita en el label (ej. "Series"). */
  suffix?: string;
}) {
  return (
    <div>
      <label className="text-[10px] text-muted-foreground tracking-[0.05em] uppercase block mb-1">
        {label}
      </label>
      <div
        className={`flex items-center bg-card border rounded-lg overflow-hidden ${
          warning ? "border-amber-500" : "border-border"
        }`}
      >
        <input
          type="number"
          value={value}
          onChange={(e) => {
            const n = parseInt(e.target.value, 10);
            onChange(Number.isFinite(n) ? n : 0);
          }}
          className="flex-1 min-w-0 px-2 py-1 bg-transparent text-sm tabular-nums focus:outline-none"
        />
        {suffix && (
          <span className="pr-2 text-[10px] text-muted-foreground tabular-nums">
            {suffix}
          </span>
        )}
      </div>
    </div>
  );
}
